import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { collection, query, where, onSnapshot, updateDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../components/firebase';
import { useAuth } from '../../context/AuthContext';
import { FiBriefcase, FiMapPin, FiClock, FiCheck, FiX, FiMail, FiUser } from 'react-icons/fi';
import { toast } from 'react-toastify';

const STATUS_STYLES = {
    pending: { label: 'Pending', color: '#eab308', bg: 'rgba(234,179,8,0.1)', border: 'rgba(234,179,8,0.25)' },
    accepted: { label: 'Accepted', color: '#22c55e', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.25)' },
    declined: { label: 'Declined', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.25)' },
};

const MyOffers = () => {
    const { user } = useAuth();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionId, setActionId] = useState(null);

    // Real-time listener on offers collection
    useEffect(() => {
        if (!user?.uid) return;
        const q = query(collection(db, 'offers'), where('studentId', '==', user.uid));
        const unsub = onSnapshot(q, snap => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
            setOffers(data);
            setLoading(false);
        });
        return () => unsub();
    }, [user]);

    const respond = async (offer, newStatus) => {
        setActionId(offer.id);
        try {
            await updateDoc(doc(db, 'offers', offer.id), { status: newStatus });

            // Notify the company
            await addDoc(collection(db, 'notifications'), {
                title: newStatus === 'accepted' ? '✅ Offer Accepted' : '❌ Offer Declined',
                message: `${offer.studentName || 'A student'} has ${newStatus} your offer for "${offer.jobTitle}".`,
                type: newStatus === 'accepted' ? 'success' : 'warning',
                recipient: 'specific',
                recipientUid: offer.companyId,
                timestamp: serverTimestamp(),
                createdAt: serverTimestamp(),
                readBy: [],
            });

            toast.success(newStatus === 'accepted' ? '🎉 Offer accepted!' : 'Offer declined.');
        } catch (err) {
            toast.error('Failed to update offer');
        }
        setActionId(null);
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                Loading offers...
            </div>
        );
    }

    if (offers.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <FiBriefcase size={44} style={{ marginBottom: '16px', color: 'var(--text-muted)', opacity: 0.4 }} />
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-main)', margin: '0 0 8px' }}>No offers yet</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0 }}>
                    When a company sends you an offer, it'll show up here.
                </p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {offers.map((offer, idx) => {
                const s = STATUS_STYLES[offer.status] || STATUS_STYLES.pending;
                const isPending = offer.status === 'pending';
                const isActing = actionId === offer.id;

                return (
                    <motion.div
                        key={offer.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.04 }}
                        style={{
                            background: 'var(--bg-card)',
                            border: `1px solid var(--border)`,
                            borderRadius: '18px',
                            padding: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '14px',
                        }}
                    >
                        {/* Top Row */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                    <div style={{
                                        width: '36px', height: '36px', borderRadius: '10px',
                                        background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: 'var(--accent)', flexShrink: 0,
                                    }}>
                                        <FiBriefcase size={16} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-main)', lineHeight: 1.2 }}>
                                            {offer.jobTitle}
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                            <FiUser size={10} /> {offer.companyName}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {offer.jobType && (
                                        <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(34,197,94,0.1)', color: 'var(--accent)', fontWeight: 700 }}>
                                            {offer.jobType}
                                        </span>
                                    )}
                                    {offer.jobLocation && (
                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                            <FiMapPin size={10} /> {offer.jobLocation}
                                        </span>
                                    )}
                                    {offer.jobStipend && (
                                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{offer.jobStipend}</span>
                                    )}
                                </div>
                            </div>

                            <div style={{
                                padding: '5px 12px', borderRadius: '20px',
                                background: s.bg, border: `1px solid ${s.border}`,
                                color: s.color, fontSize: '11px', fontWeight: 800,
                                flexShrink: 0, whiteSpace: 'nowrap',
                            }}>
                                {s.label}
                            </div>
                        </div>

                        {/* Personal note */}
                        {offer.note && (
                            <div style={{
                                padding: '12px 14px', borderRadius: '12px',
                                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                                display: 'flex', gap: '10px', alignItems: 'flex-start',
                            }}>
                                <FiMail size={13} style={{ color: 'var(--accent)', marginTop: '2px', flexShrink: 0 }} />
                                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-main)', lineHeight: 1.6 }}>
                                    "{offer.note}"
                                </p>
                            </div>
                        )}

                        {/* Timestamp + Actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-muted)' }}>
                                <FiClock size={10} />
                                {offer.createdAt?.toDate
                                    ? offer.createdAt.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : 'Just now'}
                            </div>

                            {isPending && (
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <motion.button
                                        whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                        disabled={isActing}
                                        onClick={() => respond(offer, 'declined')}
                                        style={{
                                            height: '36px', padding: '0 16px', borderRadius: '10px',
                                            background: 'transparent', border: '1.5px solid var(--border)',
                                            color: 'var(--text-muted)', fontSize: '13px', fontWeight: 700,
                                            cursor: isActing ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            opacity: isActing ? 0.5 : 1,
                                        }}
                                    >
                                        <FiX size={14} /> Decline
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                        disabled={isActing}
                                        onClick={() => respond(offer, 'accepted')}
                                        style={{
                                            height: '36px', padding: '0 18px', borderRadius: '10px',
                                            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                            border: 'none', color: '#fff', fontSize: '13px', fontWeight: 800,
                                            cursor: isActing ? 'not-allowed' : 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            boxShadow: '0 4px 10px rgba(34,197,94,0.25)',
                                            opacity: isActing ? 0.7 : 1,
                                        }}
                                    >
                                        <FiCheck size={14} /> {isActing ? 'Updating...' : 'Accept'}
                                    </motion.button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};

export default MyOffers;
